import { getJson, sendJson } from '@/lib/api';

/**
 * The development tracker (Roy's portal list, item 1; 2026-09-27). Mirrors the
 * API's `dev-tracker.domain.ts`: the stages, in order, and the areas.
 */

export const DEV_STAGE = {
  notStarted: 'not_started',
  inDevelopment: 'in_development',
  bceReview: 'bce_review',
  revisions: 'revisions',
  approved: 'approved',
  live: 'live',
} as const;
export type DevStage = (typeof DEV_STAGE)[keyof typeof DEV_STAGE];

/** The stages in order, with the words people see. */
export const DEV_STAGE_OPTIONS: readonly { readonly key: DevStage; readonly label: string }[] = [
  { key: DEV_STAGE.notStarted, label: 'Not started' },
  { key: DEV_STAGE.inDevelopment, label: 'In development' },
  { key: DEV_STAGE.bceReview, label: 'BCE review' },
  { key: DEV_STAGE.revisions, label: 'Revisions' },
  { key: DEV_STAGE.approved, label: 'Approved' },
  { key: DEV_STAGE.live, label: 'Live' },
];

export const DEV_AREA = {
  clientPortal: 'client_portal',
  adminApp: 'admin_app',
  website: 'website',
  salesforce: 'salesforce',
  integrations: 'integrations',
  other: 'other',
} as const;
export type DevArea = (typeof DEV_AREA)[keyof typeof DEV_AREA];

export const DEV_AREA_OPTIONS: readonly { readonly key: DevArea; readonly label: string }[] = [
  { key: DEV_AREA.clientPortal, label: 'Client portal' },
  { key: DEV_AREA.adminApp, label: 'Admin app' },
  { key: DEV_AREA.website, label: 'Website' },
  { key: DEV_AREA.salesforce, label: 'Salesforce' },
  { key: DEV_AREA.integrations, label: 'Integrations' },
  { key: DEV_AREA.other, label: 'Other' },
];

export const stageLabel = (stage: DevStage): string =>
  DEV_STAGE_OPTIONS.find((option) => option.key === stage)?.label ?? stage;
export const areaLabel = (area: DevArea): string => DEV_AREA_OPTIONS.find((option) => option.key === area)?.label ?? area;

/** One move between stages. */
export interface DevStageEvent {
  readonly stage: DevStage;
  /** The day it reached the stage, `YYYY-MM-DD`. */
  readonly on: string;
  readonly recordedBy: string;
  readonly recordedAt: string;
  readonly note?: string;
}

/** A feature, as `/dev-tracker/features` sends it. */
export interface DevFeature {
  readonly id: string;
  readonly title: string;
  readonly summary: string;
  readonly area: DevArea;
  readonly stage: DevStage;
  readonly stageSince: string | null;
  readonly liveOn: string | null;
  readonly stageDates: Partial<Record<DevStage, string>>;
  readonly history: readonly DevStageEvent[];
  readonly links: readonly string[];
  readonly createdAt: string;
}

export interface DevFeatureInput {
  readonly title: string;
  readonly summary: string;
  readonly area: DevArea;
  readonly links: readonly string[];
}

const BASE = '/dev-tracker/features';

export const listDevFeatures = (signal?: AbortSignal): Promise<DevFeature[]> => getJson<DevFeature[]>(BASE, undefined, signal);

export const createDevFeature = (input: DevFeatureInput & { readonly stage: DevStage; readonly on: string }): Promise<DevFeature> =>
  sendJson<DevFeature>('post', BASE, input);

export const updateDevFeature = (id: string, input: DevFeatureInput): Promise<DevFeature> =>
  sendJson<DevFeature>('patch', `${BASE}/${id}`, input);

export const setDevFeatureStage = (
  id: string,
  move: { readonly stage: DevStage; readonly on: string; readonly note?: string },
): Promise<DevFeature> => sendJson<DevFeature>('post', `${BASE}/${id}/stage`, move);

export const removeDevFeature = (id: string): Promise<void> => sendJson<void>('delete', `${BASE}/${id}`);

/** Today as `YYYY-MM-DD`, in the viewer's own time zone: "today" is the day they are recording. */
export function todayIso(now: Date = new Date()): string {
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

/** `2026-09-27` → "Sep 27, 2026". */
export function readableDay(day: string | null): string {
  if (day === null) return '';
  return new Date(`${day}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}
